import { postRequest } from '../api';
import { ROUTES } from '../constants';
import {
  ErrorContent,
  returnServiceTypes,
  RETURN_ERR_CODE,
} from '../constants/returns';

import {
  orderEligibilityRequest,
  ssrReturnCreateRequest,
  ssrHrtvPolicyValidatorRequest,
} from '../api/requestCreator';
import {
  setReturnsPageInfo,
  setDestroyForCreditCount,
  setReturnsAnalyticsErrors,
} from '../analytics/gatherService';
import { getIsUserLoggedIn } from '../utils/cookie';
import { safelyAccessNestedProperties } from '../utils';

const {
  FETCH_REQUEST,
  ELIGIBILITY_SUCCESS,
  SSR_CREATE_SUCCESS,
  RETURNS_400_ERROR,
  RETURNS_500_ERROR,
  HRTV_SUCCESS,
  BACK_TO_LANDING,
  CHECKBOX_SELECT,
  DROPDOWN_SELECT,
  SET_LANDING_ERROR,
  SET_ERRORS,
  BOXES_SELECT,
  SET_SELECTED_BOXES_ERROR,
  SSR_CREATE_ERROR,
  SSR_RESET,
  SSR_ERROR,
} = returnServiceTypes;

export const fetchReturnEligibility = (
  history: Object,
  orderId: String,
  emailId: String
) => (dispatch: Function) => {
  const { url, data } = orderEligibilityRequest(emailId, orderId);

  const pathname = getIsUserLoggedIn()
    ? ROUTES.PATH.ORDER_HISTORY_RETURNS
    : ROUTES.PATH.ONLINE_RETURNS;
  history.push({ pathname, state: { orderId, emailId } });
  // dispatch loading indicator on request
  dispatch({
    type: 'FETCH_RETURN_REQUEST',
    filter: 'ssrEligibility',
  });

  postRequest(url, data).then(
    (response: Object) => {
      const {
        returnService: { orderDetails, status },
      } = response.data;

      if (status === 'fail') {
        dispatch({
          type: RETURNS_400_ERROR,
          payload: {
            message: 'GENERAL_ERROR',
            analyticsErrorEvent: 'general',
          },
          filter: 'ssrEligibility',
        });
        setReturnsPageInfo('returnsError');
        setReturnsAnalyticsErrors('general');
      } else {
        dispatch({
          type: ELIGIBILITY_SUCCESS,
          payload: { response: orderDetails, page: 'landing' },
          filter: 'ssrEligibility',
        });
        // on success, navigate to online returns landing page
        setReturnsPageInfo('landing');
      }
    },
    (error: Object) => {
      // show error page and banner
      const { status } = error.response;
      if (status >= 500) {
        dispatch({
          type: SSR_CREATE_ERROR,
          payload: { page: 'error', showBannerError: true },
          filter: 'ssrEligibility',
        });
        setReturnsAnalyticsErrors('ssr_start_err');
      } else {
        dispatch({
          type: RETURNS_400_ERROR,
          payload: {
            message: 'GENERAL_ERROR',
            analyticsErrorEvent: 'general',
          },
          filter: 'ssrEligibility',
        });
        setReturnsAnalyticsErrors('general');
        //   // Go to SSR pages
        // history.push(ROUTES.URL[url]);
      }
      setReturnsPageInfo('landing', true);
    }
  );
};

export const fetchSsrReturnCreate = () => (
  dispatch: Function,
  getState: Function
) => {
  const { returnService } = getState();

  const {
    ssrItemsOptions: { dfcItemCount: destroyForCreditItemCount },
  } = returnService;

  const {
    ssrEligibility: {
      actionData: { orderId, emailId },
    },
  } = returnService;

  // dispatch loading indicator on request
  dispatch({
    type: FETCH_REQUEST,
    filter: 'ssrCreate',
  });

  const { data, url } = ssrReturnCreateRequest(orderId, emailId, returnService);
  postRequest(url, data).then(
    (response: Object) => {
      const {
        returnService: { status },
      } = response.data;

      if (status === 'fail') {
        dispatch({
          type: RETURNS_400_ERROR,
          payload: {
            message: 'GENERAL_ERROR',
            analyticsErrorEvent: 'general',
          },
          filter: 'ssrCreate',
        });
        setReturnsAnalyticsErrors('general');
      } else {
        dispatch({
          type: SSR_CREATE_SUCCESS,
          payload: { response, page: 'ssrCreate' },
          filter: 'ssrCreate',
        });
      }
      setReturnsPageInfo('returnsCreate');
    },
    (error: Object) => {
      const { status } = error.response;
      const errors = safelyAccessNestedProperties(
        ['response', 'data', 'returnService', 'errorData', 'errors', 'error'],
        error
      ) || [{}];
      const { code } = errors[0];
      if (status >= 500) {
        if (code === RETURN_ERR_CODE.INVALID_RETURN_ADDRESS) {
          dispatch({
            type: RETURNS_400_ERROR,
            payload: {
              message: ErrorContent.INVALID_VENDOR_ADDRESS_MESSAGE,
              analyticsErrorEvent: 'ssr_submission_err',
            },
            filter: 'ssrCreate',
          });
        } else {
          dispatch({ type: RETURNS_500_ERROR, filter: 'ssrCreate' });
        }
        setReturnsAnalyticsErrors('ssr_submission_err');
      } else {
        dispatch({
          type: RETURNS_400_ERROR,
          payload: {
            message: 'GENERAL_ERROR',
            analyticsErrorEvent: 'ssr_submission_err',
          },
          filter: 'ssrCreate',
        });
        setReturnsAnalyticsErrors('general');
      }
      setReturnsPageInfo('returnsCreate', true);
    }
  );
  if (destroyForCreditItemCount > 0)
    setDestroyForCreditCount(destroyForCreditItemCount);
};

export const returnsSubmitHrtv = () => (
  dispatch: Function,
  getState: Function
) => {
  const state = getState();
  const { returnService: returnsState } = state;

  const {
    ssrEligibility: {
      actionData: { orderId },
    },
  } = returnsState;

  const payload = ssrHrtvPolicyValidatorRequest(orderId, returnsState);
  const { url, data } = payload;

  // dispatch loading indicator on request
  dispatch({
    type: FETCH_REQUEST,
    filter: 'ssrHrtv',
  });

  postRequest(url, data).then(
    (response: Object) => {
      const {
        returnService: {
          ssrHRTVResponse: { ssrHrtvRespLineItems, status },
        },
      } = response.data;

      if (status === 'fail') {
        dispatch({
          type: RETURNS_400_ERROR,
          payload: {
            message: 'GENERAL_ERROR',
            analyticsErrorEvent: 'general',
          },
          filter: 'ssrHrtv',
        });
        setReturnsAnalyticsErrors('general');
      } else {
        dispatch({
          type: HRTV_SUCCESS,
          payload: { response: ssrHrtvRespLineItems, page: 'hrtv' },
          filter: 'ssrHrtv',
        });
      }
      setReturnsPageInfo('eligibility');
    },
    (error: Object) => {
      const { status } = error.response;
      if (status >= 500) {
        dispatch({
          type: RETURNS_500_ERROR,
          filter: 'ssrHrtv',
        });
        setReturnsAnalyticsErrors('ssr_eligibility_err');
      } else {
        dispatch({
          type: RETURNS_400_ERROR,
          payload: {
            message: 'GENERAL_ERROR',
            analyticsErrorEvent: 'general',
          },
          filter: 'ssrHrtv',
        });
        setReturnsAnalyticsErrors('general');
      }
      setReturnsPageInfo('eligibility', true);
    }
  );
};

export const handleBackClick = () => ({
  type: BACK_TO_LANDING,
  filter: 'ssrCreate',
});

export const handleClickCheckbox = (itemId: String) => ({
  type: CHECKBOX_SELECT,
  payload: itemId,
  filter: 'ssrEligibility',
});

export const handleDropdownSelect = (
  itemId: String,
  key: string,
  value: string
) => ({
  type: DROPDOWN_SELECT,
  payload: { itemId, key, value },
});

export const setLandingError = () => (dispatch: Function) => {
  dispatch({
    type: SET_LANDING_ERROR,
    filter: 'ssrEligibility',
    message: ErrorContent.NO_ITEMS_SELECTED,
  });
  setReturnsAnalyticsErrors('ssr_no_items');
};

export const setReturnsErrors = (returnsOptions: Object) => ({
  type: SET_ERRORS,
  payload: returnsOptions,
});

export const handleBoxSelection = (
  id: string,
  dropdownType: string,
  value: string
) => ({
  type: BOXES_SELECT,
  payload: { id, dropdownType, value },
  filter: 'ssrCreate',
});

export const handleSelection = (
  id: string,
  dropdownType: string,
  value: string
) => (dispatch: Function) => {
  dispatch({
    type: BOXES_SELECT,
    payload: { id, dropdownType, value },
    filter: 'ssrCreate',
  });
};

export const handleNoBoxesSelected = () => (dispatch: Function) => {
  dispatch({
    type: SET_SELECTED_BOXES_ERROR,
    filter: 'ssrCreate',
    message: ErrorContent.NO_BOXES_SELECTED,
  });
  setReturnsAnalyticsErrors('ssr_dropdown_unselected');
};

export const overWeightError = () => (dispatch: Function) => {
  dispatch({
    type: SSR_ERROR,
    payload: {
      analyticsErrorEvent: 'general',
    },
  });
};

export const noPaymentMethodError = () => (dispatch: Function) => {
  dispatch({
    type: SSR_ERROR,
    payload: {
      analyticsErrorEvent: 'general',
    },
  });
};

export const resetAllSsrReturns = () => (dispatch: Function) => {
  dispatch({ type: SSR_RESET, filter: 'ssrEligibility' });
  dispatch({ type: SSR_RESET, filter: 'ssrHrtv' });
  dispatch({ type: SSR_RESET, filter: 'ssrCreate' });
};

export const handleGiftCardError = () => (dispatch: Function) => {
  dispatch({
    type: SSR_ERROR,
    payload: {
      message: 'GIFTCARD',
      analyticsErrorEvent: 'ssr_giftcard',
    },
  });
};

export const handleMultiVendorError = () => (dispatch: Function) => {
  dispatch({
    type: SSR_ERROR,
    payload: {
      message: 'MULTIVENDOR',
      analyticsErrorEvent: 'ssr_m_vendor',
    },
  });
};
